import clsx from 'clsx';
import React, {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import styled from '@emotion/styled';

import useTable from '../../hooks/useTable';
import EmptyState from '../Cards/EmptyState';
import RightTableSlider from './TableComponent/RightTableSlider';
import TableFilterAndSearchBox from './TableComponent/TableFilterAndSearchBox';
import { TableLoadingSkeleton } from './TableComponent/TableLoadingSkeleton';
import TableMoreOptions from './TableComponent/TableMoreOptions';
import TablePagination from './TableComponent/TablePagination';
import { TableContext, TableProvider } from './TableContext';
import { TableGroupContext } from './TableGroup';
import { TableGroupTitles } from './TableGroupTitles';
import { TableTitleWrapperContext } from './TableTitleWrapperContext';

interface TableAttr {
  type: 'regular' | 'filter' | 'custom-filter';
  comp: React.ReactNode;
}

export interface EmptyStateMsg {
  label: string;
  optionalComp?: React.ReactNode;
  icon?: React.ReactNode;
}

export interface TableFilterBoxProps {
  leftAttr?: TableAttr[];
  rightAttr?: TableAttr[];
  showSearchBox?: boolean;
  searchBoxPlaceholder?: string;
  customActionBtns?: React.ReactNode;
  filterBoxBottomBorder?: boolean;
}

interface TableProps {
  tableTitle?: string;
  showHeader?: boolean;
  dummyData?: Record<string, any>[];
  tableHeader?: Record<string, string>;
  moreOptionsComponents?: React.ReactNode[];
  emptyStateMsg?: EmptyStateMsg | string;
  emptyStateCustomComp?: React.ReactNode;
  cellsCustomComp?: Record<string, React.ReactNode>;
  hasClickableRow?: boolean;
  tableDataSourceUrl?: string;
  size?: number;
  defaultNumOfPagination?: number;
  clickRowAction?: (...args: any) => void;
  clickRowShowSliderComp?: (args: Record<string, any>) => React.ReactNode;
  border?: boolean;
  bgColor?: string;
  filterBoxBottomBorder?: boolean;
  tableFilterBoxFeatures?: TableFilterBoxProps;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
  mobileCellComp?: React.ReactNode;
  showPaginationControl?: boolean;
  className?: string;
  continuousView?: boolean;
  borderRadius?: string;
  headerBgColor?: string;
  firstColumnOffset?: string;
  height?: number;
  rmHeaderBorder?: boolean;
  addRowBorder?: boolean;
  stickyHeader?: boolean;
  tableColumnWidth?: Record<string, string>;
  standalone?: boolean;
}

const Table: React.FC<TableProps> = ({ showHeader, ...props }) => {
  return (
    <TableProvider>
      <TableRaw {...props} />
    </TableProvider>
  );
};

interface TableRawProps extends TableProps {}

const TableRaw: React.FC<TableRawProps> = ({
  tableHeader = { 'Logirate Table': 'LogirateTable' },
  moreOptionsComponents,
  emptyStateMsg,
  emptyStateCustomComp,
  cellsCustomComp,
  hasClickableRow,
  tableDataSourceUrl,
  dummyData,
  size = 10,
  defaultNumOfPagination,
  clickRowAction,
  clickRowShowSliderComp,
  border,
  bgColor,
  filterBoxBottomBorder,
  tableFilterBoxFeatures,
  mobileCellComp,
  mobileOnlyView = false,
  showPaginationControl = true,
  tableTitle,
  className,
  continuousView,
  borderRadius,
  headerBgColor,
  firstColumnOffset,
  height,
  rmHeaderBorder = false,
  addRowBorder = false,
  stickyHeader = false,
  disableMobileComp = false,
  tableColumnWidth,
  standalone = false,
}) => {
  const tableContext = useContext(TableContext);
  const tableGroupContext = useContext(TableGroupContext);
  const tableTitleWrapperContext = useContext(TableTitleWrapperContext);
  const [page, setPage] = useState<number>(1);
  const [hasStartedInitialFetch, setHasStartedInitialFetch] =
    useState<boolean>(false);
  const hasStaticData = Array.isArray(dummyData);

  const {
    endPage,
    tableData,
    loading: tableLoading,
    // setLoading,
    fetchTableData,
  } = useTable();

  const tableColumns = useMemo(() => Object.keys(tableHeader), [tableHeader]);
  const tableColumnKeys = useMemo(
    () => Object.values(tableHeader),
    [tableHeader],
  );

  const gridTemplateColumns = useMemo(() => {
    if (!tableColumnWidth) return undefined;
    return tableColumns.map((col) => tableColumnWidth[col] ?? '1fr').join(' ');
  }, [tableColumns, tableColumnWidth]);

  const resolvedEmptyState = useMemo<EmptyStateMsg>(() => {
    if (typeof emptyStateMsg === 'string') {
      return {
        label: emptyStateMsg,
        optionalComp: emptyStateCustomComp,
      };
    }

    if (emptyStateMsg) {
      return {
        ...emptyStateMsg,
        optionalComp:
          emptyStateMsg.optionalComp === undefined
            ? emptyStateCustomComp
            : emptyStateMsg.optionalComp,
      };
    }

    return {
      label: 'Table is empty',
      optionalComp: emptyStateCustomComp,
    };
  }, [emptyStateCustomComp, emptyStateMsg]);

  const resolvedTableData = useMemo<Record<string, any>[]>(() => {
    if (Array.isArray(dummyData)) {
      return dummyData;
    }

    if (Array.isArray(tableData)) {
      return tableData;
    }

    return [];
  }, [dummyData, tableData]);

  useEffect(() => {
    if (!hasStaticData && tableContext?.tableFilterUrl) {
      setHasStartedInitialFetch(true);
      fetchTableData(tableContext?.tableFilterUrl);
    }
  }, [
    fetchTableData,
    hasStaticData,
    tableContext?.tableFilterUrl,
    tableContext?.refreshTable,
  ]);

  useEffect(() => {
    if (hasStaticData) {
      setHasStartedInitialFetch(true);
    }
  }, [hasStaticData]);

  useEffect(() => {
    tableContext?.setTableBaseUrl(tableDataSourceUrl);
    if (page && size) {
      tableContext?.handleSetTableFilterRecord({
        page: page - 1,
        size,
      });
    } else {
      tableContext?.setTableFilterUrl(tableDataSourceUrl);
    }
    // eslint-disable-next-line
  }, [page, size, tableDataSourceUrl]);

  useEffect(() => {
    tableTitleWrapperContext?.setViewShowMore(resolvedTableData.length > 0);
  }, [resolvedTableData.length, tableTitleWrapperContext]);

  const handleClickRowShowSlider = useCallback(
    (comp: React.ReactNode) => {
      tableContext?.setShowTableSlider(true);
      tableContext?.setSliderComp(comp);
    },
    [tableContext],
  );

  const handleRowClick = useCallback(
    (row: Record<string, any>) => {
      clickRowAction && clickRowAction(row);

      if (clickRowShowSliderComp) {
        handleClickRowShowSlider(clickRowShowSliderComp(row));
      }
    },
    [clickRowAction, clickRowShowSliderComp, handleClickRowShowSlider],
  );

  const handleRowKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, row: Record<string, any>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRowClick(row);
      }
    },
    [handleRowClick],
  );

  const isRowClickable = Boolean(
    hasClickableRow || clickRowAction || clickRowShowSliderComp,
  );

  const showLoadingState =
    !hasStaticData && (!hasStartedInitialFetch || tableLoading);

  return (
    <Container
      border={border}
      borderRadius={borderRadius}
      className={clsx(
        className,
        height && 'has-height',
        showLoadingState && 'is-loading',
      )}
    >
      {tableFilterBoxFeatures && (
        <TableFilterAndSearchBox
          {...tableFilterBoxFeatures}
          filterBoxBottomBorder={filterBoxBottomBorder}
        />
      )}
      {/* Table segment control */}
      {tableGroupContext && !standalone && <TableGroupTitles />}

      <TableOfContent
        className={clsx(
          className,
          height && 'y-scrollable-table',
          showLoadingState && 'is-loading',
        )}
        bgColor={bgColor}
        showPaginationControl={showPaginationControl}
        height={height}
        columns={tableColumns.length}
        gridTemplateColumns={gridTemplateColumns}
        continuousView={continuousView}
        disableMobileComp={disableMobileComp}
        mobileOnlyView={mobileOnlyView}
      >
        {tableTitle && (
          <TableLabelBox>
            <TableLabel>{tableTitle}</TableLabel>
          </TableLabelBox>
        )}

        {/* Table Header section */}
        <TableOfContentHeader
          border={border}
          bgColor={headerBgColor}
          firstColumnOffset={firstColumnOffset}
          rmHeaderBorder={rmHeaderBorder}
          className={clsx(
            className,
            tableFilterBoxFeatures ? 'zero-top-border' : '',
            stickyHeader ? 'sticky-header' : '',
          )}
          continuousView={continuousView}
          disableMobileComp={disableMobileComp}
          mobileOnlyView={mobileOnlyView}
        >
          {tableColumns.map((col) => (
            <div key={col}>{col}</div>
          ))}
        </TableOfContentHeader>

        {/* Table rows section */}
        {showLoadingState ? (
          <TableStatusContainer aria-live='polite' aria-busy='true'>
            <TableLoadingSkeleton />
          </TableStatusContainer>
        ) : resolvedTableData.length === 0 ? (
          <TableStatusContainer>
            <EmptyState
              label={resolvedEmptyState.label}
              optionalComp={resolvedEmptyState.optionalComp}
              icon={resolvedEmptyState.icon}
            />
          </TableStatusContainer>
        ) : (
          <>
            {resolvedTableData.map((row, index) => {
              const rowKey = String(row?.id ?? row?._id ?? index);
              return (
                <TableRowUnitData
                  key={rowKey}
                  className={clsx(className)}
                  hasClickableRow={isRowClickable}
                  continuousView={continuousView}
                  disableMobileComp={disableMobileComp}
                  mobileOnlyView={mobileOnlyView}
                  addRowBorder={addRowBorder}
                  isLastRow={index === resolvedTableData.length - 1}
                  firstColumnOffset={firstColumnOffset}
                  onClick={
                    isRowClickable ? () => handleRowClick(row) : undefined
                  }
                  onKeyDown={
                    isRowClickable
                      ? (event) => handleRowKeyDown(event, row)
                      : undefined
                  }
                  role={isRowClickable ? 'button' : undefined}
                  tabIndex={isRowClickable ? 0 : -1}
                >
                  {/* Table row  */}
                  {tableColumnKeys.map((col) => {
                    const customCell = cellsCustomComp?.[col];

                    if (customCell) {
                      return React.Children.map(
                        customCell,
                        (child: any, childIndex) =>
                          React.cloneElement(child, {
                            ...child.props,
                            key: `${rowKey}-${col}-${childIndex}`,
                            data: row,
                          }),
                      );
                    }

                    let computedRowData = row[col];
                    if (
                      computedRowData === undefined ||
                      computedRowData === null ||
                      computedRowData === ''
                    ) {
                      computedRowData = (
                        <EmptyCell className={clsx(className)}>N/A</EmptyCell>
                      );
                    }

                    return (
                      <div key={`${rowKey}-${col}`}>{computedRowData}</div>
                    );
                  })}

                  {/* Table row more option comp */}
                  {moreOptionsComponents && (
                    <TableMoreOptions
                      moreOptionsComponents={moreOptionsComponents}
                      data={row}
                    ></TableMoreOptions>
                  )}
                </TableRowUnitData>
              );
            })}

            {/* Mobile responsiveness */}
            {((!disableMobileComp && !continuousView) || mobileOnlyView) && (
              <MobileCellContainer mobileOnlyView={mobileOnlyView}>
                {resolvedTableData.map((row, index) => {
                  return (
                    mobileCellComp &&
                    React.Children.map(mobileCellComp, (child: any) => {
                      return React.cloneElement(child, {
                        ...child.props,
                        key: row?.id ?? row?._id ?? index,
                        data: row,
                        moreOptionsComponents: moreOptionsComponents,
                      });
                    })
                  );
                })}
              </MobileCellContainer>
            )}
          </>
        )}
      </TableOfContent>
      {/* Right slider Component */}
      <RightTableSlider />
      {endPage !== 0 && showPaginationControl && (
        <TablePagination
          className={className}
          endPage={endPage}
          defaultNumOfPagination={defaultNumOfPagination}
          changePage={setPage}
        />
      )}
    </Container>
  );
};

export default Table;

interface ContainerProps {
  border?: boolean;
  borderRadius?: string;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  border: ${({ border }) => border && `1px solid #E2E2E2`};
  padding: ${({ border }) => border && `24px`};
  border-radius: ${({ borderRadius }) => borderRadius || '8px'};
  overflow: hidden;

  &.scrollable-table {
    width: 100%;
    min-width: max-content;
    position: relative;
  }

  &.scrollable-table.is-loading {
    min-width: unset;
  }
`;

interface TableOfContentProps {
  columns?: number;
  gridTemplateColumns?: string;
  border?: boolean;
  bgColor?: string;
  showPaginationControl?: boolean;
  continuousView?: boolean;
  height?: number;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
}
const TableOfContent = styled.div<TableOfContentProps>`
  display: grid;
  grid-template-columns: ${({ columns, gridTemplateColumns }) =>
    gridTemplateColumns ?? `repeat(${columns}, 1fr)`};
  align-content: start;
  width: 100%;
  background-color: ${({ bgColor }) => (!bgColor ? `white` : bgColor)};
  word-break: break-word;

  &.y-scrollable-table {
    height: ${({ height }) => (height ? `${height}px` : '100%')};
    overflow-y: auto;
  }
  &.sticky-header {
    position: relative;
  }

  &.scrollable-table {
    min-width: max-content;
  }

  &.scrollable-table.is-loading {
    min-width: unset;
  }

  border-bottom: ${({ showPaginationControl, theme }) =>
    !showPaginationControl
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};

  & > * {
    grid-column: ${({ columns }) => `1/${(columns ?? 0) + 1}`};
  }

  @media (max-width: 884px) {
    display: ${({ continuousView, disableMobileComp, mobileOnlyView }) =>
      mobileOnlyView || (!continuousView && !disableMobileComp)
        ? 'flex'
        : 'grid'};
    flex-direction: ${({ continuousView, mobileOnlyView }) =>
      mobileOnlyView || !continuousView ? 'column' : 'unset'};
    background-color: transparent;
  }
`;

export const TableLabelSection = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

export const TableLabel = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #2b2b2b;
  width: fit-content;

  @media (max-width: 884px) {
    font-size: 16px;
  }
`;

export const TableLabelBox = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

interface TableOfContentHeaderProps {
  border?: boolean;
  rmHeaderBorder?: boolean;
  continuousView?: boolean;
  bgColor?: string;
  firstColumnOffset?: string;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
}

export const TableOfContentHeader = styled.div<TableOfContentHeaderProps>`
  display: ${({ mobileOnlyView }) => (mobileOnlyView ? 'none' : 'grid')};
  grid-template-columns: inherit;
  /* Share the parent's column tracks so header and rows stay aligned */
  grid-template-columns: subgrid;
  border-bottom: ${({ rmHeaderBorder, theme }) =>
    rmHeaderBorder
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};
  border-top: ${({ border, theme }) =>
    border
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};
  word-break: break-word;
  padding-left: 0px;
  background-color: ${({ bgColor }) => (!bgColor ? `none` : bgColor)};
  height: fit-content;

  &.sticky-header {
    /* border: 1px solid red; */
    position: sticky;
    top: 0px;
    z-index: 1;
  }

  &.scrollable-table {
    border: 1px solid transparent;
    border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  }

  & > div {
    padding: 14px;
    font-weight: 500;
    color: #5c5963;

    &:first-of-type {
      padding-left: ${({ firstColumnOffset }) => firstColumnOffset ?? '0px'};
    }
  }

  &.scrollable-table > div {
    padding-left: 14px;
    color: #5c5963;
    /* border: 1px solid red; */
  }

  @media (max-width: 884px) {
    display: ${({ continuousView, disableMobileComp, mobileOnlyView }) =>
      mobileOnlyView
        ? 'none'
        : continuousView || disableMobileComp
          ? 'grid'
          : 'none'};
  }
`;

interface TableRowUnitDataProp {
  hasClickableRow?: boolean;
  children: ReactNode;
  continuousView?: boolean;
  firstColumnOffset?: string;
  addRowBorder?: boolean;
  isLastRow?: boolean;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
}

export const TableRowUnitData = styled.div<TableRowUnitDataProp>`
  position: relative;
  display: ${({ mobileOnlyView }) => (mobileOnlyView ? 'none' : 'grid')};
  grid-template-columns: inherit;
  /* Share the parent's column tracks so header and rows stay aligned */
  grid-template-columns: subgrid;
  align-items: center;
  cursor: ${({ hasClickableRow }) => (hasClickableRow ? 'pointer' : 'default')};
  border-bottom: ${({ addRowBorder, isLastRow, theme }) =>
    !addRowBorder || isLastRow
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};

  height: fit-content;

  & > div {
    padding: 14px 16px;
    color: ${({ theme }) => theme.palette.tertiaryTextColor};

    &:first-of-type {
      padding-left: ${({ firstColumnOffset }) => firstColumnOffset ?? '0px'};
    }
  }

  &.scrollable-table > div {
    padding-left: 14px;
    color: #6e7079;
  }

  &:hover {
    background-color: #fafafa;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.logiratePrimary};
    outline-offset: -2px;
  }

  @media (max-width: 884px) {
    display: ${({ continuousView, disableMobileComp, mobileOnlyView }) =>
      mobileOnlyView
        ? 'none'
        : continuousView || disableMobileComp
          ? 'grid'
          : 'none'};
  }
`;

export const TableLabelCustomComp = styled.div`
  display: flex;
  gap: 16px;
`;

const EmptyCell = styled.div`
  color: ${({ theme }) => theme.palette.tertiaryTextColor};
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  /* border: 1px solid red; */

  &.scrollable-table {
    color: #6e7079;
  }
`;

const MobileCellContainer = styled.div<{ mobileOnlyView?: boolean }>`
  flex-direction: column;
  display: ${({ mobileOnlyView }) => (mobileOnlyView ? 'flex' : 'none')};

  @media (max-width: 884px) {
    display: flex;

    & > * {
      background-color: white;
    }
  }
`;

const TableStatusContainer = styled.div`
  padding: 38px;
  min-width: 0;
  overflow: hidden;
`;
